import { computed, makeObservable, observable } from 'mobx'
import { Cubit } from './core'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { ImpersonationInfo } from '../types'
import { Section } from '../models/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import type { PublicUser } from '../models/PublicUser'
import type { StaticModelCollection } from '../firestore-mobx/model'
import {
  getSectionsStreamForInstructor,
  getSectionsStreamForInstructors,
} from '../firestore/Section'
import { getSectionsStreamForCollaborator } from '../firestore/Section'
import { getAssistedInstructorIDs } from '../firestore/UserProfile'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import { guardOnLoaded } from '../util/guardOn'

const startedAt = (section: Section) =>
  section.data?.sectionStartDate?.getTime() ?? 0

/**
 * Everything the appeals dashboard needs that is not the appeal itself.
 *
 * The appeals come from Postgres; the names on them do not. This owns the
 * Firestore side — which sections the instructor can review, the assignments
 * inside them, and the participants who filed — so the page can turn a row of
 * ids into a row a person can read.
 */
export class InstructorAppealsCubit extends Cubit {
  repository: FirebaseRepository
  impersonationInfo?: ImpersonationInfo

  sectionsFromBeingInstructor: StaticModelCollection<Section>
  sectionsFromUsersIAssist: StaticModelCollection<Section>
  sectionsFromBeingCollaborator: StaticModelCollection<Section>

  assignmentsForSection = observable.map<
    string,
    StaticModelCollection<SectionAssignment>
  >()

  constructor(
    repository: FirebaseRepository,
    { impersonationInfo }: { impersonationInfo?: ImpersonationInfo } = {}
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.impersonationInfo = impersonationInfo

    this.sectionsFromBeingInstructor = Section.emptyCollection(repository)
    this.sectionsFromUsersIAssist = Section.emptyCollection(repository)
    this.sectionsFromBeingCollaborator = Section.emptyCollection(repository)
  }

  initialize(): void {
    this.addStream(
      getSectionsStreamForInstructor(this.repository, this.impersonationInfo),
      (sections) => {
        this.sectionsFromBeingInstructor.replaceModels(sections)
        sections.forEach((section) => this.ensureAssignmentStream(section))
      },
      {
        name: 'appeals-sections-instructor',
        onError: () => this.sectionsFromBeingInstructor.replaceModels([]),
      }
    )

    this.addStream(
      getSectionsStreamForCollaborator(
        this.repository,
        this.impersonationInfo?.userId ?? this.repository.uid
      ),
      (sections) => {
        this.sectionsFromBeingCollaborator.replaceModels(sections)
        sections.forEach((section) => this.ensureAssignmentStream(section))
      },
      {
        name: 'appeals-sections-collaborator',
        onError: () => this.sectionsFromBeingCollaborator.replaceModels([]),
      }
    )

    // A TA reviews the sections of the instructors they assist, so their
    // section list depends on a lookup that can change while the page is open.
    this.addStream(
      getAssistedInstructorIDs(
        this.repository,
        this.impersonationInfo?.userId ?? this.repository.uid
      ),
      (instructorIds) => {
        const name = 'appeals-sections-assisted'
        this.removeStream(name)

        if (instructorIds.length === 0) {
          this.sectionsFromUsersIAssist.replaceModels([])
          return
        }

        this.addStream(
          getSectionsStreamForInstructors(this.repository, instructorIds),
          (sections) => {
            this.sectionsFromUsersIAssist.replaceModels(sections)
            sections.forEach((section) => this.ensureAssignmentStream(section))
          },
          {
            name,
            onError: () => this.sectionsFromUsersIAssist.replaceModels([]),
          }
        )
      },
      {
        name: 'appeals-assisted-instructor-ids',
        onError: () => this.sectionsFromUsersIAssist.replaceModels([]),
      }
    )
  }

  private ensureAssignmentStream(section: Section) {
    const name = `appeals-section-${section.id}-assignments`
    if (this.hasStream(name)) return

    this.assignmentsForSection.set(
      section.id,
      SectionAssignment.emptyCollection(this.repository)
    )

    this.addStream(
      getSectionAssignments(this.repository, { sectionId: section.id }),
      (assignments) => {
        this.assignmentsForSection.get(section.id)?.replaceModels(assignments)
      },
      {
        name,
        disableCaptureException: true,
        onError: () => {
          this.assignmentsForSection.get(section.id)?.replaceModels([])
        },
      }
    )
  }

  /**
   * The sections whose appeals this person may review, newest term first —
   * both the dropdown's options and the set the dashboard fetches appeals for.
   */
  @computed
  get reviewableSections() {
    return guardOnLoaded(
      [
        this.sectionsFromBeingInstructor,
        this.sectionsFromUsersIAssist,
        this.sectionsFromBeingCollaborator,
      ] as const,
      ([mine, assisted, collaborating]) => {
        const byId = new Map<string, Section>()
        for (const section of [...mine, ...assisted, ...collaborating]) {
          byId.set(section.id, section)
        }

        return [...byId.values()].sort((a, b) => startedAt(b) - startedAt(a))
      }
    )
  }

  sectionById(sectionId: string): Section | undefined {
    if (!this.reviewableSections.isLoaded) return undefined
    return this.reviewableSections.value.find(
      (section) => section.id === sectionId
    )
  }

  assignmentById(
    sectionId: string,
    assignmentId: string
  ): SectionAssignment | undefined {
    return this.assignmentsForSection
      .get(sectionId)
      ?.models.find((assignment) => assignment.id === assignmentId)
  }

  /**
   * Participants by Firestore uid. The store hands back placeholder models and
   * fills them in, so callers can render immediately and let names arrive.
   */
  participantsByUserId(userIds: string[]): Map<string, PublicUser> {
    const unique = [...new Set(userIds)]
    const users = this.repository.userStore.getUsers(unique)
    return new Map(unique.map((userId, index) => [userId, users[index]]))
  }
}
